import API from 'utils/api';
import { onError } from 'utils/handlers';
import formatValues, { formatReceived } from 'utils/formatValues';

export const baseURI = 'recaudacion/cargos-en-ejecuciones-pae/';

/**
 * Construye el objeto para `datos_del_cargo_y_relacionados_json` a partir de los valores del cargo
 * @param {object} values
 * @returns {object}
 */
export const buildDatosCargoRelacionados = (values) => {
  const allDescuentos = [
    ...(values.descuentos_aplicables || []),
    ...(values.descuentos_especiales || []),
    ...(values.recargos || []),
    ...(values.recargosMulta || []),
    ...(values.actualizaciones || []),
  ];
  const descuentosAplicados = allDescuentos.filter((d) => d && d.aplicado);

  return {
    id: values.id,
    descuentosAplicados,
    tipo_de_cargo_clave: values.tipo_de_cargo?.id || values.tipo_de_cargo?.clave,
    total_descuentos_aplicados: descuentosAplicados
      .reduce((acc, curr) => acc + (curr?.importe_total || curr?.importe || 0), 0),
    sistema: values?.tipo_de_cargo?.sistemas?.descripcion,
    periodo_fiscal_anual: values?.tipo_de_cargo?.periodo_fiscal?.periodo,
    descuentos_sobre_cargo: [
      ...(values.descuentos_aplicables || []),
      ...(values.descuentos_especiales || []),
    ]
      .filter((d) => d && d.aplicado)
      .map((d) => ({ id: d.id, importe: d.importe })),
    recargos: (values.recargos || []).map((r) => ({
      id: r.id,
      importe: r.importe,
    })),
    descuentos_sobre_recargo: (values.recargos || [])
      .map((r) => r?.descuentos || [])
      .flat(2)
      .filter((d) => d && d.aplicado)
      .map((d) => ({
        id: d.id,
        importe: d.importe,
      })),
    recargos_multa: (values.recargosMulta || []).map((r) => ({
      id: r.id,
      importe: r.importe,
    })),
    descuentos_sobre_recargo_multa: (values.recargosMulta || [])
      .map((r) => r?.descuentos || [])
      .flat(2)
      .filter((d) => d && d.aplicado)
      .map((d) => ({
        id: d.id,
        importe: d.importe,
      })),
    actualizaciones: (values.actualizaciones || []).map((a) => ({
      id: a.id,
      importe: a.importe,
    })),
    descuentos_sobre_actualizacion: (values.actualizaciones || [])
      .map((a) => a?.descuentos || [])
      .flat(2)
      .filter((d) => d && d.aplicado)
      .map((d) => ({
        id: d.id,
        importe: d.importe,
      })),
    gastos: (values.gastos || []).map((g) => ({
      id: g.id,
      importe: g.importe,
    })),
    descuentos_sobre_gasto: (values.gastos || [])
      .map((g) => g?.descuentos || [])
      .flat(2)
      .filter((d) => d && d.aplicado)
      .map((d) => ({
        id: d.id,
        importe: d.importe,
      })),
    gastos_multa: (values.gastosMulta || []).map((g) => ({
      id: g.id,
      importe: g.importe,
    })),
    descuentos_sobre_gasto_multa: (values.gastosMulta || [])
      .map((g) => g?.descuentos || [])
      .flat(2)
      .filter((d) => d && d.aplicado)
      .map((d) => ({
        id: d.id,
        importe: d.importe,
      })),
    convenio: values.convenios,
    tramite: values.tramite,
    descripcion: values.descripcion,
  };
};

/**
 * GET Cargos en ejecución
 * @param {object} params
 * @returns {Promise<object>}
 */
export const getCargosEnEjecucion = async (params) => {
  try {
    const response = await API.get(baseURI, { params });

    return formatReceived(response.data).map((item) => ({
      ...item,
      _cargo: typeof item?.datos_del_cargo_y_relacionados_json === 'string'
        ? JSON.parse(item?.datos_del_cargo_y_relacionados_json)
        : item?.datos_del_cargo_y_relacionados_json,
    }));
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Cargo en ejecución
 * @param {number} id
 * @returns {Promise<object>}
 */
export const getCargoEnEjecucion = async (id) => {
  try {
    const response = await API.get(`${baseURI}${id}`);
    return formatReceived(response.data);
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * POST Cargo en ejecución
  * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const postCargoEnEjecucion = async (values) => {
  try {
    const dataProcessed = {
      cargo: values.id,
      ejecucion: values.ejecucion,
      fecha_del_importe: new Date().toISOString(),
      fecha_de_vencimiento_del_cargo: values?.tipo_de_cargo?.fecha_de_vencimiento
        ? new Date(values?.tipo_de_cargo?.fecha_de_vencimiento).toISOString()
        : new Date().toISOString(),
      importe_de_cargo_base: values.importe || 0,
      pagado_base: 0,
      adeudo_base: values.importe || 0,
      importe_del_descuento_base: values._descuentos_totales_junto_accesorios || 0,
      sub_total_base: (values.importe || 0) - (values._descuentos_totales_junto_accesorios || 0),
      importe_de_actualizacion: values.actualizaciones_base || 0,
      pagado_actualizacion: 0,
      importe_del_descuento_de_actualizacion: values.actualizaciones
        ?.map((a) => a.descuentos
          ?.filter((d) => d.aplicado && !d.es_multa)
          ?.reduce((acc, curr) => acc + curr.importe_total, 0) || 0)
        ?.reduce((acc, curr) => acc + curr, 0) || 0,
      adeudo_de_actualizacion: values.actualizaciones_base || 0,
      sub_total_actualizacion: (values.actualizaciones_base || 0) - (values.actualizaciones
        ?.map((a) => a.descuentos
          ?.filter((d) => d.aplicado && !d.es_multa)
          ?.reduce((acc, curr) => acc + curr.importe_total, 0) || 0)
        ?.reduce((acc, curr) => acc + curr, 0) || 0),
      importe_de_recargo: values.recargos_base || 0,
      pagado_recargo: 0,
      adeudo_de_recargo: values.recargos_base || 0,
      importe_del_descuento_del_recargo: 0,
      sub_total_recargo: values.recargos_base || 0,
      importe_de_recargo_multa: values.multas_base || 0,
      pagado_recargo_multa: 0,
      adeudo_de_recargo_multa: values.multas_base || 0,
      importe_del_descuento_del_recargo_multa: 0,
      datos_del_cargo_y_relacionados_json: buildDatosCargoRelacionados(values),
      sub_total_recargo_multa: values.multas_base || 0,
      importe_de_gasto: values.gastos_totales || 0,
      pagado_gasto: 0,
      adeudo_de_gasto: values.gastos_totales || 0,
      importe_del_descuento_de_gasto: 0,
      sub_total_gasto: values.gastos_totales || 0,
      importe_de_gasto_multa: 0,
      pagado_gasto_multa: 0,
      adeudo_de_gasto_multa: 0,
      importe_del_descuento_de_gasto_multa: 0,
      sub_total_gasto_multa: 0,
      total_del_cargo: parseFloat(parseFloat(values.adeudo_total).toFixed(2)) || 0,
      cargo_padre_del_accesorio_id: values?.cargo_padre || null,
      es_accesorio: values?.es_accesorio || false,
    };
    const formatedValues = formatValues(dataProcessed, {
      clean: false,
    });

    const response = await API.post(baseURI, formatedValues);
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};
